import {
  TrendingUp,
  Building,
  FileText,
  AlertCircle,
  MessageSquare,
  Camera,
  BarChart3,
  FolderOpen,
  FileCheck,
  HardHat,
  Users,
  ClipboardCheck,
  ListChecks,
  Bug,
  DollarSign,
} from "lucide-react";

export const ROLES = {
  ADMIN: "admin",
  USER_INTERNAL: "user_internal",
  USER_EXTERNAL: "user_external",
};

const ALL_TABS = [
  { id: "dashboard", label: "Dashboard", icon: TrendingUp, path: "/dashboard" },
  { id: "projects", label: "Projects", icon: Building, path: "/projects" },
  {
    id: "inspections",
    label: "Inspections",
    icon: ClipboardCheck,
    path: "/inspections",
  },
  { id: "ncr", label: "NCRs", icon: AlertCircle, path: "/ncrs" },
  { id: "rfi", label: "RFIs", icon: MessageSquare, path: "/rfis" },
  { id: "photos", label: "Photos", icon: Camera, path: "/photos" },
  {
    id: "documents",
    label: "Document Control",
    icon: FolderOpen,
    path: "/documents",
  },
  { id: "materials", label: "Materials", icon: FileCheck, path: "/materials" },
  {
    id: "submittals",
    label: "Submittals",
    icon: FileText,
    path: "/submittals",
  },
  { id: "analytics", label: "Analytics & Reports", icon: BarChart3, path: "/analytics" },
  { id: "safety", label: "Safety", icon: HardHat, path: "/safety" },
  {
    id: "daily",
    label: "Report",
    icon: FileText,
    path: "/daily-reports",
  },
  { id: "users", label: "Users", icon: Users, path: "/users" },
  { id: "bug-reports", label: "Bug Reports", icon: Bug, path: "/bug-reports" },
  { id: "ai-usage", label: "AI Cost & Limits", icon: DollarSign, path: "/ai-usage" },
  {
    id: "audit-logs",
    label: "Audit Logs",
    icon: ListChecks,
    path: "/audit-logs",
  },

  {
    id: "inspection-templates",
    label: "Inspection Templates",
    icon: ListChecks,
    path: "/inspection-templates",
  },

  {
    id: "internal-rfi",
    label: "Internal RFI",
    icon: ListChecks,
    path: "/internal-rfi",
  },
  {
    id: "internal-submittals",
    label: "Internal Submittals",
    icon: FileText,
    path: "/internal-submittals",
  },
];

// Global nav visibility per SYSTEM ROLE

const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: ALL_TABS.map((tab) => tab.id),

  // Internal users = Smoky Hill internal team
  [ROLES.USER_INTERNAL]: [
    "dashboard",
    "projects",
    "inspections",
    "ncr",
    "rfi",
    "photos",
    "documents",
    "submittals",
    "analytics",
    "safety",
    "daily",
    "inspection-templates",
    "internal-rfi",
  ],

  // External users = contractors / clients / external stakeholders
  [ROLES.USER_EXTERNAL]: [
    "dashboard",
    "projects",
    "rfi",
    "ncr",
    "submittals",
    "documents",
  ],
};

// System-level permissions per SYSTEM ROLE

const ALL_PERMISSIONS = {
  [ROLES.ADMIN]: {
    create: ["all"],
    read: ["all"],
    update: ["all"],
    delete: ["all"],
    approve: ["all"],
    export: ["all"],
  },

  [ROLES.USER_INTERNAL]: {
    // Internal team can operate on core QA/QC resources globally
    create: [
      "inspections",
      "ncr",
      "rfi",
      "photos",
      "daily",
      "submittals",
      "safety",
      "materials",
      "inspection-templates",
    ],
    read: [
      "projects",
      "inspections",
      "ncr",
      "rfi",
      "documents",
      "materials",
      "photos",
      "submittals",
      "daily",
      "safety",
      "analytics",
      "inspection-templates",
    ],
    update: [
      "inspections",
      "ncr",
      "rfi",
      "documents",
      "materials",
      "photos",
      "submittals",
      "safety",
      "inspection-templates",
    ],
    delete: ["photos", "documents"],
    approve: [
      // approvals are usually project-role based
    ],
    export: ["inspections", "reports"],
  },

  [ROLES.USER_EXTERNAL]: {
    // External users, very limited global powers
    create: ["rfi", "submittals"],
    read: ["projects", "rfi", "submittals", "documents", "photos", "ncr"],
    update: ["rfi", "submittals", "ncr"],
    delete: [],
    approve: [],
    export: [],
  },
};

export const getTabsForRole = (systemRole) => {
  const role = systemRole || ROLES.USER_EXTERNAL;
  const allowedTabIds =
    ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS[ROLES.USER_EXTERNAL];
  return ALL_TABS.filter((tab) => allowedTabIds.includes(tab.id));
};

export const hasTabAccess = (systemRole, tabId) => {
  const role = systemRole || ROLES.USER_EXTERNAL;
  const allowedTabs =
    ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS[ROLES.USER_EXTERNAL];
  return allowedTabs.includes(tabId);
};

export const hasPermission = (systemRole, action, resource) => {
  const role = systemRole || ROLES.USER_EXTERNAL;

  const rolePermissions =
    ALL_PERMISSIONS[role] || ALL_PERMISSIONS[ROLES.USER_EXTERNAL];

  if (!rolePermissions) {
    console.warn(`No permissions defined for role: ${role}`);
    return false;
  }

  const actions = rolePermissions[action];

  if (!actions || !Array.isArray(actions)) {
    return false;
  }

  return actions.includes("all") || actions.includes(resource);
};

export const getRoleDisplayName = (systemRole) => {
  const roleNames = {
    [ROLES.ADMIN]: "Admin",
    [ROLES.USER_INTERNAL]: "User (Internal)",
    [ROLES.USER_EXTERNAL]: "User (External)",
  };
  return roleNames[systemRole] || systemRole;
};

export default ALL_TABS;
